// River-Boat decor (Level 8): bright Fall-Guys style river course.
// Player hops between moving wooden boats across stretches of blue
// water, with safe docks holding word gates between sections. Jumping
// fish hazards leap out of the water in the middle stretches.
//
// Reuses the MovingPlatform ride system (same shape as space-ship)
// for the boats — Game already registers them with platformOffsetS /L
// so the player can land, run, and jump off them naturally.

import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { Path } from "./Path";
import type { MovingPlatform, PlatformState } from "./hitParadeDecor";
import { isLowQuality } from "./quality";

const RIVER_LOW = isLowQuality();

const DOCK_TOP_Y = 0.18;

/** Water level (in arena-local Y). Anything below this is "in the
 *  river" — the player drowning detection in the bite/contact logic
 *  uses this to know when the player hits the surface. */
const WATER_Y = -0.3;

/** Procedural ripple-texture (canvas). Concentric soft-blue ring noise
 *  that scrolls via UV offsets for a moving-water effect without
 *  needing a shader or image asset. Singleton — built once. */
let _waterRippleTex: THREE.CanvasTexture | null = null;
function buildWaterRippleTexture(): THREE.CanvasTexture {
  if (_waterRippleTex) return _waterRippleTex;
  const SIZE = 256;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = SIZE;
  const ctx = canvas.getContext("2d")!;
  // Base — translucent blue.
  ctx.fillStyle = "#3aa3ff";
  ctx.fillRect(0, 0, SIZE, SIZE);
  // Sprinkle bright soft circles to read as caustics.
  for (let i = 0; i < 90; i++) {
    const x = Math.random() * SIZE;
    const y = Math.random() * SIZE;
    const r = 12 + Math.random() * 40;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, "rgba(255,255,255,0.65)");
    grad.addColorStop(0.45, "rgba(180,225,255,0.25)");
    grad.addColorStop(1, "rgba(58,163,255,0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Dark wavy streaks for depth.
  ctx.fillStyle = "rgba(20, 60, 120, 0.18)";
  for (let i = 0; i < 14; i++) {
    const y = (i / 14) * SIZE;
    ctx.fillRect(0, y, SIZE, 1 + Math.random() * 2);
  }
  _waterRippleTex = new THREE.CanvasTexture(canvas);
  _waterRippleTex.colorSpace = THREE.SRGBColorSpace;
  return _waterRippleTex;
}
const DOCK_THICKNESS = 0.55;
const ISLAND_LIFT = DOCK_TOP_Y + 0.55 - DOCK_THICKNESS / 2; // 0.455 — matches space-ship
const DOCK_PLANK = 0xb47a3d;       // warm wood
const DOCK_TRIM = 0x7c4a1f;        // dark wood trim
const WATER_COLOR = 0x39a0ff;
const WATER_FOAM = 0x9adcff;
const BOAT_HULL = 0xa66b3a;
const BOAT_RAIL = 0xdcb070;
const BOAT_INSIDE = 0x5f3a1c;
const FISH_PINK = 0xff6bbd;
const FISH_ORANGE = 0xff8a3d;

interface Fish {
  s: number;
  side: 1 | -1;
  group: THREE.Group;
  /** Period in ms — one full jump cycle. */
  periodMs: number;
  phase: number;
  /** Cached world position for collision. */
  worldX: number;
  worldY: number;
}

export interface RiverBoatHandle {
  group: THREE.Group;
  /** Solid wooden dock meshes — always-supported. */
  supportMeshes: THREE.Object3D[];
  /** Moving boats — registered with Game.platforms for player rides. */
  platforms: MovingPlatform[];
  gatePositions: number[];
  tick: (clockMs: number, dt: number) => void;
  /** Fish-jump contact for the player at (s, l, worldY). */
  fishContact: (s: number, l: number, worldY: number, radius: number) => {
    normalL: number;
    normalS: number;
    penetration: number;
    barVelL: number;
    barVelS: number;
  } | null;
  /** Returns true when the patrolling shark is currently overlapping
   *  the player's footprint. A true value should kill the player and
   *  trigger a checkpoint respawn — caller owns that logic. */
  sharkBite: (s: number, l: number, worldY: number, radius: number) => boolean;
  /** World-Y of the river surface in arena-local coords. */
  waterY: number;
  /** Tell the shark where the player is + whether they're currently
   *  splashing through water. When in water, the shark redirects from
   *  its patrol arc toward the player and jumps more aggressively. */
  setSharkTarget: (s: number, l: number, inWater: boolean) => void;
}

function buildDock(width: number, depth: number, isFinish = false): THREE.Group {
  const g = new THREE.Group();
  // Planks: a big slab, with diagonal "strap" lines hinted via a few
  // thinner cross beams on top.
  const slab = new THREE.Mesh(
    new SoftBox(width, DOCK_THICKNESS, depth),
    new THREE.MeshStandardMaterial({ color: DOCK_PLANK, roughness: 0.6 }),
  );
  slab.receiveShadow = true;
  slab.castShadow = true;
  g.add(slab);
  // Cross-plank lines (cosmetic only).
  const lineMat = new THREE.MeshStandardMaterial({ color: DOCK_TRIM, roughness: 0.7 });
  const plankCount = Math.max(3, Math.round(depth / 1.4));
  for (let i = 0; i < plankCount; i++) {
    const line = new THREE.Mesh(
      new SoftBox(width * 0.95, 0.06, 0.18),
      lineMat,
    );
    const z = -depth / 2 + (i + 0.5) * (depth / plankCount);
    line.position.set(0, DOCK_THICKNESS / 2 + 0.01, z);
    g.add(line);
  }
  // Side trim posts (look like dock pylons).
  const postMat = new THREE.MeshStandardMaterial({ color: DOCK_TRIM, roughness: 0.65 });
  for (const sx of [-1, 1] as const) {
    for (const sz of [-1, 1] as const) {
      const post = new THREE.Mesh(
        new THREE.CylinderGeometry(0.18, 0.22, 1.1, 10),
        postMat,
      );
      post.position.set(sx * (width / 2 - 0.18), -0.25, sz * (depth / 2 - 0.18));
      g.add(post);
    }
  }
  if (isFinish) {
    // Big colourful FINISH arch.
    const archMat = new THREE.MeshStandardMaterial({
      color: 0xffd166,
      emissive: 0xff8a3d,
      emissiveIntensity: 0.32,
    });
    for (const x of [-width * 0.45, width * 0.45]) {
      const post = new THREE.Mesh(new SoftBox(0.7, 4.4, 0.7), archMat);
      post.position.set(x, 2.2 + DOCK_THICKNESS / 2, 0);
      g.add(post);
    }
    const banner = new THREE.Mesh(
      new SoftBox(width * 1.0, 1.2, 0.5),
      new THREE.MeshStandardMaterial({ color: 0xfff3a8, emissive: 0xffd166, emissiveIntensity: 0.4 }),
    );
    banner.position.set(0, 4.7 + DOCK_THICKNESS / 2, 0);
    g.add(banner);
    // Pennant flags around the arch.
    const pennantColors = [0xff5fa7, 0xffd166, 0x6bd0ff, 0x6be09b, 0xa855f7];
    for (let i = 0; i < 12; i++) {
      const flag = new THREE.Mesh(
        new THREE.PlaneGeometry(0.5, 0.7),
        new THREE.MeshStandardMaterial({
          color: pennantColors[i % pennantColors.length]!,
          side: THREE.DoubleSide,
        }),
      );
      flag.position.set(-width / 2 + (i / 11) * width, 5.4 + DOCK_THICKNESS / 2, 0.05);
      flag.rotation.y = Math.PI;
      g.add(flag);
    }
  } else {
    // Small flagpole + welcome flag on mid-docks.
    const pole = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07, 0.07, 2.6, 8),
      new THREE.MeshStandardMaterial({ color: 0xb47a3d, roughness: 0.5 }),
    );
    pole.position.set(width / 2 - 0.4, 1.3, depth / 2 - 0.4);
    g.add(pole);
    const flag = new THREE.Mesh(
      new THREE.PlaneGeometry(0.9, 0.6),
      new THREE.MeshStandardMaterial({ color: 0xff5fa7, side: THREE.DoubleSide }),
    );
    flag.position.set(width / 2 - 0.85, 2.2, depth / 2 - 0.4);
    g.add(flag);
  }
  return g;
}

function buildBoat(halfWidth: number, halfDepth: number, thickness: number): THREE.Group {
  const g = new THREE.Group();
  // Hull base — wider at the deck, slightly narrower below.
  const hull = new THREE.Mesh(
    new SoftBox(halfWidth * 2, thickness, halfDepth * 2),
    new THREE.MeshStandardMaterial({ color: BOAT_HULL, roughness: 0.5 }),
  );
  hull.castShadow = true;
  hull.receiveShadow = true;
  g.add(hull);
  // Interior darker plank to give visual depth.
  const inside = new THREE.Mesh(
    new SoftBox(halfWidth * 1.85, 0.18, halfDepth * 1.85),
    new THREE.MeshStandardMaterial({ color: BOAT_INSIDE, roughness: 0.7 }),
  );
  inside.position.y = thickness / 2 - 0.08;
  g.add(inside);
  // Rim plank running along the sides + ends.
  const rimMat = new THREE.MeshStandardMaterial({ color: BOAT_RAIL, roughness: 0.45 });
  // Long rims (along Z)
  for (const sx of [-1, 1] as const) {
    const rim = new THREE.Mesh(
      new SoftBox(0.25, 0.36, halfDepth * 2),
      rimMat,
    );
    rim.position.set(sx * (halfWidth - 0.05), thickness / 2 + 0.18, 0);
    g.add(rim);
  }
  // End rims
  for (const sz of [-1, 1] as const) {
    const rim = new THREE.Mesh(
      new SoftBox(halfWidth * 2, 0.36, 0.25),
      rimMat,
    );
    rim.position.set(0, thickness / 2 + 0.18, sz * (halfDepth - 0.05));
    g.add(rim);
  }
  // Rope coil at one end for character.
  const rope = new THREE.Mesh(
    new THREE.TorusGeometry(0.28, 0.08, 8, 16),
    new THREE.MeshStandardMaterial({ color: 0xeee4c1, roughness: 0.7 }),
  );
  rope.rotation.x = Math.PI / 2;
  rope.position.set(0, thickness / 2 + 0.4, halfDepth - 0.5);
  g.add(rope);
  return g;
}

function buildFish(): THREE.Group {
  const g = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.SphereGeometry(0.5, 14, 10),
    new THREE.MeshStandardMaterial({
      color: FISH_ORANGE,
      emissive: FISH_ORANGE,
      emissiveIntensity: 0.18,
      roughness: 0.45,
    }),
  );
  body.scale.set(1, 0.7, 1.4);
  body.castShadow = true;
  g.add(body);
  const tail = new THREE.Mesh(
    new THREE.ConeGeometry(0.3, 0.55, 6),
    new THREE.MeshStandardMaterial({ color: FISH_PINK, roughness: 0.45 }),
  );
  tail.rotation.x = -Math.PI / 2;
  tail.position.z = -0.7;
  g.add(tail);
  // Eye dot.
  const eye = new THREE.Mesh(
    new THREE.SphereGeometry(0.08, 8, 6),
    new THREE.MeshBasicMaterial({ color: 0x0a0a0a }),
  );
  eye.position.set(0.16, 0.18, 0.4);
  g.add(eye);
  return g;
}

function buildBoatMovingPlatform(
  path: Path,
  gap: { start: number; end: number },
  clockOrigin: number,
  opts: { phaseOffset: number; lateral: number },
): MovingPlatform {
  const halfWidth = 2.8;
  const halfDepth = 4.4;
  const thickness = 0.55;
  const group = buildBoat(halfWidth, halfDepth, thickness);
  const sStart = gap.start + halfDepth - 0.6;
  const sEnd = gap.end - halfDepth + 0.6;
  const periodMs = 14000;
  let lastS = sStart;
  let velocityS = 0;
  const tick = (dt: number): PlatformState => {
    const t = (Date.now() - clockOrigin) / periodMs + opts.phaseOffset;
    const phase = (t % 1) * Math.PI * 2;
    const eased = (1 - Math.cos(phase)) / 2;
    const s = sStart + (sEnd - sStart) * eased;
    velocityS = dt > 0 ? (s - lastS) / dt : 0;
    lastS = s;
    const pos = path.positionAt(s, opts.lateral);
    const topY = DOCK_TOP_Y + thickness;
    // Gentle vertical bob so the boat looks afloat (visual only — the
    // top Y the player rides at stays fixed so the ride feels stable).
    const bob = Math.sin((Date.now() - clockOrigin) / 700 + opts.phaseOffset * Math.PI) * 0.06;
    group.position.set(pos.x, pos.y + thickness / 2 + bob, pos.z);
    group.rotation.y = path.yawAt(s);
    group.rotation.z = bob * 0.4;
    return {
      s,
      centerL: opts.lateral,
      topY,
      halfDepth,
      halfWidth,
      halfHeight: thickness / 2,
      velocityS,
    };
  };
  return { group, tick };
}

/** Build a big toothy shark — tapered steel-blue body, white belly,
 *  gills, pectoral + dorsal + caudal fins, and a hinged lower JAW so the
 *  patrol tick can animate it chomping. The head faces local +Z. Returns
 *  the group plus the jaw sub-group (rotate jaw.rotation.x to open). */
function buildShark(): { group: THREE.Group; jaw: THREE.Group } {
  const g = new THREE.Group();
  const topMat = new THREE.MeshStandardMaterial({ color: 0x4d82b4, roughness: 0.48, metalness: 0.06 });
  const bellyMat = new THREE.MeshStandardMaterial({ color: 0xeef4fa, roughness: 0.55 });
  const mouthMat = new THREE.MeshStandardMaterial({ color: 0x6b1f33, roughness: 0.7 });
  const gumMat = new THREE.MeshStandardMaterial({ color: 0xd9637f, roughness: 0.6 });
  const toothMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.32 });

  // Trunk — tapered torpedo capsule along local +Z.
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.82, 2.5, 14, 28), topMat);
  body.rotation.x = Math.PI / 2;
  body.position.z = -0.35;
  body.castShadow = true;
  g.add(body);
  // White belly — lighter, flattened underside.
  const belly = new THREE.Mesh(new THREE.CapsuleGeometry(0.74, 2.4, 10, 22), bellyMat);
  belly.rotation.x = Math.PI / 2;
  belly.position.set(0, -0.34, -0.35);
  belly.scale.set(1.02, 0.66, 1);
  g.add(belly);

  // Head — rounded snout block that carries the upper jaw + eyes.
  const head = new THREE.Mesh(new SoftBox(1.5, 1.18, 1.5), topMat);
  head.position.set(0, 0.08, 1.55);
  head.castShadow = true;
  g.add(head);
  const headBelly = new THREE.Mesh(new SoftBox(1.4, 0.46, 1.45), bellyMat);
  headBelly.position.set(0, -0.42, 1.6);
  g.add(headBelly);

  // Dark mouth cavity so the open jaw reads as a maw, not a gap.
  const maw = new THREE.Mesh(new SoftBox(1.3, 0.5, 1.0), mouthMat);
  maw.position.set(0, -0.42, 1.95);
  g.add(maw);

  // Upper jaw (fixed) — gum strip + downward fangs at the mouth line.
  const upper = new THREE.Group();
  upper.position.set(0, -0.46, 2.02);
  const upperGum = new THREE.Mesh(new SoftBox(1.36, 0.16, 0.5), gumMat);
  upper.add(upperGum);
  for (let i = 0; i < 7; i++) {
    const t = new THREE.Mesh(new THREE.ConeGeometry(0.085, 0.32, 6), toothMat);
    t.position.set(-0.6 + i * 0.2, -0.18, 0.14);
    t.rotation.x = Math.PI; // point down
    upper.add(t);
  }
  g.add(upper);

  // Lower jaw (hinged) — pivots at the back of the mouth and swings open.
  const jaw = new THREE.Group();
  jaw.position.set(0, -0.6, 1.35);
  const jawMesh = new THREE.Mesh(new SoftBox(1.34, 0.38, 1.25), topMat);
  jawMesh.position.set(0, -0.12, 0.55);
  jaw.add(jawMesh);
  const jawBelly = new THREE.Mesh(new SoftBox(1.24, 0.22, 1.18), bellyMat);
  jawBelly.position.set(0, -0.28, 0.55);
  jaw.add(jawBelly);
  const lowerGum = new THREE.Mesh(new SoftBox(1.3, 0.15, 0.46), gumMat);
  lowerGum.position.set(0, 0.1, 0.66);
  jaw.add(lowerGum);
  for (let i = 0; i < 7; i++) {
    const t = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.3, 6), toothMat);
    t.position.set(-0.58 + i * 0.19, 0.26, 0.66); // point up
    jaw.add(t);
  }
  g.add(jaw);

  // Eyes — glossy black beads on the head sides.
  const eyeWhiteMat = new THREE.MeshStandardMaterial({ color: 0x111417, roughness: 0.15, metalness: 0.1 });
  for (const sx of [-0.64, 0.64]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.14, 14, 12), eyeWhiteMat);
    eye.position.set(sx, 0.28, 1.95);
    g.add(eye);
    const glint = new THREE.Mesh(new THREE.SphereGeometry(0.04, 8, 6), new THREE.MeshBasicMaterial({ color: 0xffffff }));
    glint.position.set(sx + sx * 0.04, 0.36, 2.05);
    g.add(glint);
  }

  // Gills — three angled slits each side of the head.
  const gillMat = new THREE.MeshStandardMaterial({ color: 0x335f86, roughness: 0.6 });
  for (const sx of [-1, 1] as const) {
    for (let i = 0; i < 3; i++) {
      const gill = new THREE.Mesh(new SoftBox(0.05, 0.5, 0.07), gillMat);
      gill.position.set(sx * 0.8, 0.06, 0.85 - i * 0.26);
      gill.rotation.z = sx * 0.22;
      g.add(gill);
    }
  }

  // Dorsal fin — the iconic Jaws triangle.
  const finGeo = new THREE.ConeGeometry(0.7, 1.3, 4, 1);
  finGeo.rotateY(Math.PI / 4);
  const fin = new THREE.Mesh(finGeo, topMat);
  fin.position.set(0, 0.92, -0.15);
  fin.scale.set(0.55, 1.5, 1.25);
  fin.castShadow = true;
  g.add(fin);

  // Pectoral fins — swept back along the sides.
  for (const sx of [-1, 1] as const) {
    const pec = new THREE.Mesh(new THREE.ConeGeometry(0.5, 1.15, 4, 1), topMat);
    pec.rotation.z = sx * Math.PI * 0.52;
    pec.rotation.y = sx * 0.5;
    pec.position.set(sx * 0.85, -0.36, 0.5);
    pec.scale.set(0.4, 1, 0.85);
    g.add(pec);
  }

  // Caudal (tail) fin — tall vertical crescent.
  const tail = new THREE.Mesh(new THREE.ConeGeometry(0.95, 1.8, 4, 1), topMat);
  tail.rotation.x = -Math.PI / 2;
  tail.position.set(0, 0.1, -2.65);
  tail.scale.set(0.45, 1.55, 0.85);
  g.add(tail);

  return { group: g, jaw };
}

interface SharkState {
  group: THREE.Group;
  /** Hinged lower jaw — rotate .rotation.x to chomp. */
  jaw: THREE.Group;
  /** Centre arc-length the shark patrols around. */
  centerS: number;
  /** Patrol amplitude — how far up/down the course it cruises. */
  patrolHalfRange: number;
  /** Lateral half-range (typically the river half-width). */
  lateralHalfRange: number;
  /** Period for the patrol loop in ms. */
  periodMs: number;
  /** Current world (x, y, z) — published each frame for collision use. */
  worldX: number;
  worldY: number;
  worldZ: number;
  /** True while the shark is mid-jump (above water, hunting the course). */
  jumping: boolean;
}

export function buildRiverBoatCourse(path: Path, clockOrigin: number): RiverBoatHandle {
  const rb = path.riverBoat;
  if (!rb) throw new Error("buildRiverBoatCourse called on non-river-boat path");

  const group = new THREE.Group();
  const supportMeshes: THREE.Object3D[] = [];
  const platforms: MovingPlatform[] = [];
  const fishObjs: Fish[] = [];

  const dockWidth = 9.6;

  // ── Docks ──
  for (let i = 0; i < rb.docks.length; i++) {
    const d = rb.docks[i]!;
    const isFinish = i === rb.docks.length - 1;
    const len = d.end - d.start;
    const dock = buildDock(dockWidth, len, isFinish);
    dock.position.set(0, ISLAND_LIFT, d.start + len / 2);
    group.add(dock);
    const slab = dock.children[0];
    if (slab) supportMeshes.push(slab);
  }

  // ── Water plane covering the entire course length. Now uses a
  //    procedural ripple texture that scrolls forward + sideways so
  //    the river visibly flows; the surface also bobs vertically with
  //    a low-amplitude sine so it never reads as a static plane. ──
  const totalLen = rb.docks[rb.docks.length - 1]!.end;
  const waterTex = buildWaterRippleTexture();
  waterTex.wrapS = THREE.RepeatWrapping;
  waterTex.wrapT = THREE.RepeatWrapping;
  waterTex.repeat.set(6, Math.max(6, totalLen / 8));
  const waterMat = new THREE.MeshStandardMaterial({
    color: WATER_COLOR,
    emissive: WATER_FOAM,
    emissiveIntensity: 0.22,
    roughness: 0.25,
    metalness: 0.2,
    transparent: true,
    opacity: 0.92,
    map: waterTex,
  });
  // Use a subdivided plane so we can wiggle vertices for a real ripple.
  const waterGeo = new THREE.PlaneGeometry(80, totalLen + 40, 24, Math.max(24, Math.round(totalLen / 4)));
  const water = new THREE.Mesh(waterGeo, waterMat);
  water.rotation.x = -Math.PI / 2;
  water.position.set(0, -0.3, totalLen / 2);
  water.receiveShadow = true;
  group.add(water);
  // Snapshot the original vertex Y positions so the per-frame ripple
  // tick can add to them without drift.
  const posAttr = waterGeo.attributes.position as THREE.BufferAttribute;
  const waterRestY = new Float32Array(posAttr.count);
  for (let i = 0; i < posAttr.count; i++) waterRestY[i] = posAttr.getZ(i); // PlaneGeometry's local Z is up after we rotate.x
  // White foam strip down the centre of each gap so the river has a
  // sense of flow direction.
  const foamMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.35 });
  for (const g of rb.gaps) {
    const stripe = new THREE.Mesh(
      new THREE.PlaneGeometry(0.6, g.end - g.start),
      foamMat,
    );
    stripe.rotation.x = -Math.PI / 2;
    stripe.position.set(0, -0.29, (g.start + g.end) / 2);
    group.add(stripe);
  }

  // ── Moving boats: 2 per gap with offset phases + lateral lanes. ──
  for (const gap of rb.gaps) {
    const left = buildBoatMovingPlatform(path, gap, clockOrigin, {
      phaseOffset: 0,
      lateral: 2.5,
    });
    platforms.push(left);
    group.add(left.group);
    const right = buildBoatMovingPlatform(path, gap, clockOrigin, {
      phaseOffset: 0.5,
      lateral: -2.5,
    });
    platforms.push(right);
    group.add(right.group);
  }

  // ── Jumping fish hazards. ──
  for (let i = 0; i < rb.fish.length; i++) {
    const f = rb.fish[i]!;
    const fg = buildFish();
    fg.position.set(f.side * 2.8, -1, f.s);
    group.add(fg);
    fishObjs.push({
      s: f.s,
      side: f.side,
      group: fg,
      periodMs: 2400 + (i % 3) * 350,
      phase: (i * 0.31) % 1,
      worldX: f.side * 2.8,
      worldY: -1,
    });
  }

  // ── Shark patrol — fin always above water, occasional jump-attack
  //    arcs across the course (player has to time their boat hop). ──
  const sharkState: SharkState | null = (() => {
    if (rb.gaps.length === 0) return null;
    // Park the shark in the middle gap so it owns one specific stretch
    // of river. Patrols up + down + side-to-side.
    const midGap = rb.gaps[Math.floor(rb.gaps.length / 2)]!;
    const centerS = (midGap.start + midGap.end) / 2;
    const half = (midGap.end - midGap.start) / 2;
    const shark = buildShark();
    group.add(shark.group);
    return {
      group: shark.group,
      jaw: shark.jaw,
      centerS,
      patrolHalfRange: Math.max(8, half - 2),
      lateralHalfRange: rb.halfWidth - 1.6,
      periodMs: 6800,
      worldX: 0,
      worldY: -0.5,
      worldZ: centerS,
      jumping: false,
    };
  })();

  // ── Scenery: rocks, reeds, trees along the riverbank. ──
  const rockMat = new THREE.MeshStandardMaterial({ color: 0x9aa3b2, roughness: 0.85, flatShading: true });
  for (let i = 0; i < (RIVER_LOW ? 6 : 28); i++) {
    const side = i % 2 === 0 ? -1 : 1;
    const rock = new THREE.Mesh(new THREE.IcosahedronGeometry(0.6 + Math.random() * 0.7, 0), rockMat);
    rock.position.set(
      side * (rb.halfWidth + 5 + Math.random() * 4),
      -0.05,
      (i / 28) * totalLen,
    );
    rock.scale.y = 0.7;
    group.add(rock);
  }
  // Cattail reeds — green spikes near the water edge.
  const reedMat = new THREE.MeshStandardMaterial({ color: 0x3aa05c, roughness: 0.6 });
  const reedTopMat = new THREE.MeshStandardMaterial({ color: 0x7c4a1f, roughness: 0.6 });
  for (let i = 0; i < (RIVER_LOW ? 8 : 36); i++) {
    const side = i % 2 === 0 ? -1 : 1;
    const x = side * (rb.halfWidth + 3.5 + Math.random() * 2.5);
    const z = (i / 36) * totalLen + Math.random() * 2;
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.05, 1.2, 6), reedMat);
    stem.position.set(x, 0.6, z);
    group.add(stem);
    const tip = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 0.32, 8), reedTopMat);
    tip.position.set(x, 1.36, z);
    group.add(tip);
  }
  // Trees (cone + cylinder trunk) further out.
  const trunkMat = new THREE.MeshStandardMaterial({ color: 0x6b3a1f, roughness: 0.7 });
  const leafColors = [0x2bb673, 0x3aa05c, 0x6be09b];
  const treeCount = RIVER_LOW ? 4 : 14;
  for (let i = 0; i < treeCount; i++) {
    const side = i % 2 === 0 ? -1 : 1;
    const x = side * (rb.halfWidth + 9 + Math.random() * 6);
    const z = (i / treeCount) * totalLen + Math.random() * 3;
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.42, 1.8, 8), trunkMat);
    trunk.position.set(x, 0.9, z);
    group.add(trunk);
    const leaves = new THREE.Mesh(
      new THREE.ConeGeometry(1.4 + Math.random() * 0.6, 2.6, 10),
      new THREE.MeshStandardMaterial({ color: leafColors[i % leafColors.length]!, roughness: 0.6 }),
    );
    leaves.position.set(x, 2.6, z);
    group.add(leaves);
  }
  // Bunting line strung between the docks: little pennants over the river.
  const buntingColors = [0xff5fa7, 0xffd166, 0x6bd0ff, 0x6be09b, 0xa855f7];
  for (let i = 0; i < rb.gaps.length; i++) {
    const g = rb.gaps[i]!;
    for (const side of [-1, 1] as const) {
      const polePost = new THREE.Mesh(
        new THREE.CylinderGeometry(0.12, 0.14, 4.5, 10),
        new THREE.MeshStandardMaterial({ color: 0xb47a3d, roughness: 0.55 }),
      );
      polePost.position.set(side * (rb.halfWidth + 0.6), 2.0, (g.start + g.end) / 2);
      group.add(polePost);
      const flag = new THREE.Mesh(
        new THREE.PlaneGeometry(2.4, 1.6),
        new THREE.MeshStandardMaterial({
          color: buntingColors[(i + (side === 1 ? 1 : 0)) % buntingColors.length]!,
          side: THREE.DoubleSide,
        }),
      );
      flag.position.set(side * (rb.halfWidth + 1.6), 3.4, (g.start + g.end) / 2);
      flag.rotation.y = side === 1 ? -Math.PI / 2 : Math.PI / 2;
      group.add(flag);
    }
  }

  const tick = (clockMs: number, dt: number) => {
    // Animate the water — scroll the texture forward + sideways and
    // wiggle the actual vertices so light catches the surface.
    const tSec = clockMs / 1000;
    waterTex.offset.x = (tSec * 0.04) % 1;
    waterTex.offset.y = -(tSec * 0.09) % 1;
    // Per-frame vertex ripple is a CPU cost (hundreds of setZ + a buffer
    // re-upload every frame) — skip it on the low tier; the scrolling texture
    // still sells the moving water.
    if (!RIVER_LOW) {
      let dirty = false;
      const w = waterGeo.parameters.width;
      const h = waterGeo.parameters.height;
      const segW = waterGeo.parameters.widthSegments + 1;
      const segH = waterGeo.parameters.heightSegments + 1;
      for (let j = 0; j < segH; j++) {
        for (let i = 0; i < segW; i++) {
          const idx = j * segW + i;
          const u = (i / (segW - 1)) * w;
          const v = (j / (segH - 1)) * h;
          // Cheap sum-of-sines ripple — visible motion, no GPU cost.
          const bump =
            Math.sin(u * 0.08 + tSec * 1.4) * 0.06 +
            Math.sin(v * 0.05 + tSec * 0.9) * 0.05 +
            Math.sin((u + v) * 0.04 + tSec * 1.7) * 0.04;
          posAttr.setZ(idx, waterRestY[idx]! + bump);
          dirty = true;
        }
      }
      if (dirty) posAttr.needsUpdate = true;
    }
    // Shark — patrols by default; hunts the player when they're in
    // the water. Deterministic enough across clients (MP) because the
    // target & clock are shared.
    if (sharkState) {
      const sh = sharkState;
      let lateral: number;
      let s: number;
      let jumping = false;
      let yaw = 0;
      let y = -0.5;
      if (sharkTarget.inWater) {
        // HUNT MODE — steer toward the player. Smooth-track so the
        // shark doesn't teleport, just glides toward them. Lerp the
        // current world position toward the target.
        // Frame-rate normalised lerp — at 60 FPS this is ~0.045 per
        // frame; at 30 FPS the effective rate doubles to keep convergence
        // identical. Previous flat 0.045/frame meant the shark was
        // literally half-speed on 30 FPS hardware.
        const HUNT_LERP = 1 - Math.exp(-2.8 * Math.max(0, dt));
        lateral = sh.worldX + (sharkTarget.l - sh.worldX) * HUNT_LERP;
        s = sh.worldZ + (sharkTarget.s - sh.worldZ) * HUNT_LERP;
        yaw = Math.atan2(sharkTarget.l - sh.worldX, sharkTarget.s - sh.worldZ);
        // Aggressive jumps — once every ~3s, when close, leap out of
        // the water toward the player.
        const distToPlayer = Math.hypot(sharkTarget.l - lateral, sharkTarget.s - s);
        const jumpPhase = (clockMs / 3000) % 1;
        if (jumpPhase < 0.18 && distToPlayer < 14) {
          const u = jumpPhase / 0.18;
          y = -0.5 + Math.sin(u * Math.PI) * 6;
          jumping = true;
        }
      } else {
        // PATROL — slow oval around the home gap.
        const t = (clockMs / sh.periodMs) % 1;
        lateral = Math.sin(t * Math.PI * 2) * sh.lateralHalfRange;
        s = sh.centerS + Math.cos(t * Math.PI * 2) * sh.patrolHalfRange;
        const dl = Math.cos(t * Math.PI * 2) * sh.lateralHalfRange;
        const ds = -Math.sin(t * Math.PI * 2) * sh.patrolHalfRange;
        yaw = Math.atan2(dl, ds);
        const jumpPhase = (t * 2) % 1;
        if (jumpPhase < 0.18) {
          const u = jumpPhase / 0.18;
          y = -0.5 + Math.sin(u * Math.PI) * 5.5;
          jumping = true;
        }
      }
      sh.group.position.set(lateral, y, s);
      // Head (local +Z) faces the direction of travel — no +PI, which had the
      // shark swimming backwards.
      sh.group.rotation.y = yaw;
      sh.group.rotation.x = jumping ? Math.cos(((clockMs / (sharkTarget.inWater ? 3000 : sh.periodMs / 2)) % 1) * Math.PI) * 0.5 : 0;
      // Chomp the jaw: fast + wide when hunting/leaping, a slow idle gape
      // otherwise. rotation.x swings the hinged lower jaw open.
      const chompFast = jumping || sharkTarget.inWater;
      const chompRate = chompFast ? 150 : 700;
      const wave = 0.5 + 0.5 * Math.sin((clockMs / chompRate) * Math.PI * 2);
      sh.jaw.rotation.x = (chompFast ? 0.18 + 0.55 * wave : 0.06 + 0.14 * wave);
      sh.worldX = lateral;
      sh.worldY = y;
      sh.worldZ = s;
      sh.jumping = jumping;
    }
    // Fish jump arcs — deterministic from clockMs.
    for (const f of fishObjs) {
      const t = ((clockMs / f.periodMs) + f.phase) % 1;
      // Spend ~40% of cycle jumping in an arc, rest underwater.
      let x = f.side * 2.8;
      let y = -1.2;
      let rot = 0;
      if (t < 0.4) {
        const u = t / 0.4;
        // Parabolic arc above water.
        y = -1.2 + Math.sin(u * Math.PI) * 3.4;
        // Slight lateral sweep toward the centre of the river then back.
        x = f.side * 2.8 * (1 - Math.sin(u * Math.PI) * 0.55);
        rot = u * Math.PI * (f.side === 1 ? -1 : 1) * 1.1;
      }
      f.group.position.set(x, y, f.s);
      f.group.rotation.z = rot;
      f.worldX = x;
      f.worldY = y;
    }
  };

  // Fish-vs-player capsule contact (sphere approximation).
  const fishContact = (s: number, l: number, worldY: number, radius: number) => {
    const FISH_R = 0.6;
    for (const f of fishObjs) {
      // Skip if underwater.
      if (f.worldY < 0) continue;
      const dx = l - f.worldX;
      const dy = (worldY + 0.55) - f.worldY;
      const dz = s - f.s;
      const dist2 = dx * dx + dy * dy + dz * dz;
      const sumR = FISH_R + radius;
      if (dist2 >= sumR * sumR) continue;
      const dist = Math.sqrt(Math.max(0.0001, dist2));
      const nl = dx / dist;
      const ns = dz / dist;
      return {
        normalL: nl,
        normalS: ns,
        penetration: sumR - dist,
        barVelL: -nl * 4,
        barVelS: -ns * 1,
      };
    }
    return null;
  };

  /** Returns true if the shark is currently overlapping the player.
   *  Bite radius is generous while the player is splashing through
   *  water (hunt mode) so the shark CAN actually catch them. */
  const sharkBite = (s: number, l: number, worldY: number, radius: number) => {
    if (!sharkState) return false;
    // The shark can ONLY bite a player who is actually in the water — a
    // player standing on a boat or dock is safe. This must hold even while
    // the shark is mid-leap: previously the jumping branch used a 1.6m bite
    // regardless of `inWater`, so a leaping shark whose arc passed a boat
    // deck could kill a player standing safely on the boat.
    if (!sharkTarget.inWater) return false;
    // Belt-and-braces: also require the player to be down near the water
    // line (not up on an elevated deck) in case `inWater` lags a frame on a
    // slow device.
    if (worldY > WATER_Y + 0.7) return false;
    // Bite range: a bit larger while leaping so the dramatic jump connects.
    const SHARK_R = sharkState.jumping ? 1.6 : 1.4;
    const dx = l - sharkState.worldX;
    const dy = (worldY + 0.55) - sharkState.worldY;
    const dz = s - sharkState.worldZ;
    const sumR = SHARK_R + radius;
    return dx * dx + dy * dy + dz * dz < sumR * sumR;
  };

  /** Current hunt target — updated by `setSharkTarget` each frame. */
  const sharkTarget = { s: 0, l: 0, inWater: false };
  const setSharkTarget = (s: number, l: number, inWater: boolean) => {
    sharkTarget.s = s;
    sharkTarget.l = l;
    sharkTarget.inWater = inWater;
  };

  return {
    group,
    supportMeshes,
    platforms,
    gatePositions: rb.gates,
    tick,
    fishContact,
    sharkBite,
    waterY: WATER_Y,
    setSharkTarget,
  };
}
